import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { Translate } from '@shared/translation';
import type { SubmitEventHandler } from 'react';
import {
  PopperContainer,
  PopperContent,
  type PopperContentProps,
  PopperFooter,
  PopperHeading,
} from '../../Popper';
import { PresetList } from '../Presets';
import { useDateRangeTexts } from '../useDateRangeTexts';
import { Fields } from './Fields';
import { parseInputRange } from './inputFormat';
import { usePicker } from './PickerProvider';
import { StepNavigation } from './StepNavigation';

export function PickerPanel({ onClose }: PopperContentProps) {
  const texts = useDateRangeTexts();
  const { activePreset, apply, cancel, presets, setDraftValue, timeZone } =
    usePicker();

  const handleCancel = () => {
    cancel();
    onClose();
  };

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const nextValue = parseInputRange({
      from: String(formData.get('from') ?? ''),
      to: String(formData.get('to') ?? ''),
      timeZone,
    });

    if (!nextValue || nextValue.to.getTime() < nextValue.from.getTime()) {
      return;
    }

    apply(nextValue);
    onClose();
  };

  return (
    <form onSubmit={handleSubmit}>
      <PopperContainer width="auto">
        <PopperHeading>
          <Translate text={texts.picker.header} />
        </PopperHeading>
        <PopperContent>
          <Box display="flex" gap={2}>
            {presets.length > 0 && (
              <>
                <Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    <Translate text={texts.presets.header} />
                  </Typography>
                  <PresetList
                    activePreset={activePreset}
                    onChange={setDraftValue}
                    presets={presets}
                    timeZone={timeZone}
                  />
                </Box>
                <Divider orientation="vertical" flexItem variant="middle" />
              </>
            )}
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="h4" sx={{ flex: 1 }}>
                  {activePreset ? (
                    <Translate
                      text={activePreset.label}
                      values={activePreset.labelValues}
                    />
                  ) : (
                    <Translate text={texts.picker.custom} />
                  )}
                </Typography>
                <StepNavigation />
              </Box>
              <Fields />
            </Box>
          </Box>
        </PopperContent>
        <PopperFooter>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              color="inherit"
              onClick={handleCancel}
              sx={{ mr: 2 }}
              type="button"
              variant="outlined"
            >
              <Translate text={texts.picker.actions.cancel.label} />
            </Button>
            <Button variant="contained" color="primary" type="submit">
              <Translate text={texts.picker.actions.set.label} />
            </Button>
          </Box>
        </PopperFooter>
      </PopperContainer>
    </form>
  );
}
